"use client";

import * as React from "react";

/**
 * Dismissing a popup by pressing its backdrop, without depending on a click.
 *
 * Base UI dismisses on a backdrop CLICK — deliberately, so a drag that begins
 * inside the panel and ends outside it doesn't close anything. But iOS does
 * not reliably synthesize a click for a tap on a non-interactive element, and
 * under load it stops entirely: with the iPad's download handoff running,
 * three consecutive taps on the queue drawer's backdrop delivered
 * pointerdown/touchstart/touchend on the right element and produced no click
 * at all (the fourth arrived 900ms late), so the drawer ignored them.
 *
 * A press that starts on the backdrop and stays put means the same thing a
 * click does, and is built from events WebKit always delivers.
 * The drag semantics survive for free: a press starting on the panel never
 * reaches the backdrop's listeners.
 */

/** How far a press may travel and still be a tap rather than a drag. */
const MOVE_TOLERANCE_PX = 10;

export type BackdropDismissContext = { close: () => void };

const BackdropDismissCtx = React.createContext<BackdropDismissContext | null>(
  null,
);

export function BackdropDismissProvider({
  value,
  children,
}: {
  value: BackdropDismissContext;
  children: React.ReactNode;
}) {
  return (
    <BackdropDismissCtx.Provider value={value}>
      {children}
    </BackdropDismissCtx.Provider>
  );
}

/**
 * Ref callback for a popup's backdrop element.
 *
 * Stable, so React attaches once per backdrop rather than on every render of
 * the popup — these re-render on things like download progress ticks.
 */
export function useBackdropDismiss(): (element: HTMLElement | null) => void {
  const ctx = React.useContext(BackdropDismissCtx);
  const ctxRef = React.useRef(ctx);
  ctxRef.current = ctx;

  return React.useCallback((element: HTMLElement | null) => {
    if (element == null) {
      return;
    }

    // The press being watched, tagged with WHICH contact started it. A
    // hybrid iPad has several at once — a finger on the glass, a trackpad
    // cursor, a Pencil — and movement from an unrelated one must not cancel a
    // real tap. Getting that wrong reintroduces the bug this whole file
    // exists to fix: the popup stops closing.
    let press: { x: number; y: number; contact: string } | null = null;

    const pointerContact = (event: PointerEvent) =>
      `pointer:${event.pointerId}`;
    const touchContact = (touch: Touch) => `touch:${touch.identifier}`;

    // A finger fires pointerdown AND touchstart, in that order, so the touch
    // identity wins and its own touchmove is what tracks it. The finger's
    // pointermove is then ignored as "not this contact", which is harmless.
    const begin = (
      point: { clientX: number; clientY: number } | undefined,
      contact: string,
    ) => {
      press =
        point == null ? null : { x: point.clientX, y: point.clientY, contact };
    };

    const finish = (
      point: { clientX: number; clientY: number } | undefined,
    ) => {
      const from = press;
      // One tap fires BOTH families, so this doubles as the "already handled"
      // flag: whichever end event arrives first consumes the press.
      press = null;

      if (from == null || point == null) {
        return;
      }

      if (
        Math.abs(point.clientX - from.x) <= MOVE_TOLERANCE_PX &&
        Math.abs(point.clientY - from.y) <= MOVE_TOLERANCE_PX
      ) {
        // No provider means this backdrop is rendered outside our wrappers —
        // leave it to Base UI's own click path.
        ctxRef.current?.close();
      }
    };

    // Cancel as soon as the gesture travels, rather than judging it by where
    // it happened to end: a finger can swipe well off the origin and come back
    // before lifting, and comparing only the endpoints would read that as a
    // tap. The press has to STAY still, not merely finish where it started.
    const track = (
      point: { clientX: number; clientY: number } | undefined,
      contact: string,
    ) => {
      if (press == null || point == null || press.contact !== contact) {
        return;
      }

      if (
        Math.abs(point.clientX - press.x) > MOVE_TOLERANCE_PX ||
        Math.abs(point.clientY - press.y) > MOVE_TOLERANCE_PX
      ) {
        press = null;
      }
    };

    const cancel = () => {
      press = null;
    };

    // Passive listeners: this only observes. Preventing anything would
    // suppress the browser's own click, which Base UI still uses where it is
    // delivered reliably.
    // Begin and end are about the BACKDROP — a press starting on the panel
    // must never reach them, which is what preserves Base UI's drag
    // semantics. Movement is about the GESTURE, wherever it travels, so it is
    // watched on the document: a press that starts near the panel edge, moves
    // across the panel and comes back would otherwise be invisible here,
    // because the panel is a sibling and its events do not bubble through the
    // backdrop. (Touch events always target where the touch began, so only
    // pointer input actually escapes — but one path is easier to trust than a
    // rule about which input types need which listener.)
    const onBackdrop: [string, EventListener][] = [
      [
        "pointerdown",
        (event) => {
          const pointer = event as PointerEvent;
          // Primary button only — a right-click is not a dismissal.
          begin(
            pointer.button === 0 ? pointer : undefined,
            pointerContact(pointer),
          );
        },
      ],
      ["pointerup", (event) => finish(event as PointerEvent)],
      ["pointercancel", cancel],
      [
        "touchstart",
        (event) => {
          const touch = event as TouchEvent;
          // A second finger means a pinch, not a tap. Fingers landing
          // ELSEWHERE are caught by the document listener below — this only
          // sees the ones that land here.
          const first = touch.changedTouches[0];
          begin(
            touch.touches.length > 1 || first == null ? undefined : first,
            first == null ? "" : touchContact(first),
          );
        },
      ],
      ["touchend", (event) => finish((event as TouchEvent).changedTouches[0])],
      ["touchcancel", cancel],
    ];

    const onDocument: [string, EventListener][] = [
      [
        "pointermove",
        (event) =>
          track(event as PointerEvent, pointerContact(event as PointerEvent)),
      ],
      [
        "touchmove",
        (event) => {
          for (const touch of (event as TouchEvent).changedTouches) {
            track(touch, touchContact(touch));
          }
        },
      ],
      [
        // A finger landing anywhere while a press is in progress makes this
        // multi-touch, and the backdrop's own touchstart cannot see one that
        // lands on the panel. `touches` counts every active contact on the
        // document, so no per-finger bookkeeping is needed to notice.
        "touchstart",
        (event) => {
          if ((event as TouchEvent).touches.length > 1) {
            cancel();
          }
        },
      ],
    ];

    // Passive: this only observes. Preventing anything would suppress the
    // browser's own click, which Base UI still uses where it is delivered
    // reliably.
    for (const [type, handler] of onBackdrop) {
      element.addEventListener(type, handler, { passive: true });
    }

    // Only while this backdrop exists, which is only while the popup is open.
    // The handler leaves immediately unless a press is in progress.
    for (const [type, handler] of onDocument) {
      document.addEventListener(type, handler, { passive: true });
    }

    return () => {
      for (const [type, handler] of onBackdrop) {
        element.removeEventListener(type, handler);
      }

      for (const [type, handler] of onDocument) {
        document.removeEventListener(type, handler);
      }
    };
  }, []);
}
